import * as types from "./postTypes";

const initialState = {
  /** postsByUser[userId] = { items, total, page, limit } */
  postsByUser: {},
  currentPost: null,
  loadingList: false,
  loadingCurrent: false,
  loadingMutation: false,
  /** featuredByUser[userId] = { items: highlight[] } */
  featuredByUser: {},
  /** currentFeatured = { _id, Name, posts, ... } from getById */
  currentFeatured: null,
  loadingFeatured: false,
  error: null,
};

function updatePostInItems(items, postId, updater) {
  if (!Array.isArray(items)) return items;
  return items.map((p) => {
    const id = p._id?.toString?.() ?? p.id;
    if (id !== (postId?.toString?.() ?? postId)) return p;
    return updater ? updater(p) : p;
  });
}

export const userPostsReducer = (state = initialState, action) => {
  switch (action.type) {
    case types.FETCH_POSTS_BY_USER_REQUEST:
      return {
        ...state,
        loadingList: true,
        error: null,
      };
    case types.FETCH_POSTS_BY_USER_SUCCESS: {
      const { userId, items, total, page, limit } = action.payload;
      const isFirstPage = !page || page <= 1;
      const prev = state.postsByUser[userId];
      const prevItems = prev?.items ?? [];
      const newItems = items ?? [];
      const mergedItems = isFirstPage
        ? newItems
        : [
            ...prevItems,
            ...newItems.filter(
              (n) =>
                !prevItems.some(
                  (p) =>
                    (p._id?.toString?.() ?? p.id) ===
                    (n._id?.toString?.() ?? n.id),
                ),
            ),
          ];
      return {
        ...state,
        loadingList: false,
        postsByUser: {
          ...state.postsByUser,
          [userId]: {
            items: mergedItems,
            total: total ?? 0,
            page: page ?? 1,
            limit: limit ?? 10,
          },
        },
      };
    }
    case types.FETCH_POSTS_BY_USER_FAIL:
      return { ...state, loadingList: false, error: action.payload };

    case types.FETCH_POST_BY_ID_REQUEST:
      return { ...state, loadingCurrent: true, error: null };
    case types.FETCH_POST_BY_ID_SUCCESS:
      return { ...state, loadingCurrent: false, currentPost: action.payload };
    case types.FETCH_POST_BY_ID_FAIL:
      return { ...state, loadingCurrent: false, error: action.payload };

    case types.CREATE_POST_REQUEST:
    case types.UPDATE_POST_REQUEST:
    case types.DELETE_POST_REQUEST:
      return { ...state, loadingMutation: true, error: null };
    case types.CREATE_POST_SUCCESS: {
      const post = action.payload;
      const userId = post?.UserID?._id ?? post?.UserID ?? post?.userID;
      if (!userId) return { ...state, loadingMutation: false };
      const key = userId.toString?.() ?? userId;
      const prev = state.postsByUser[key] ?? {
        items: [],
        total: 0,
        page: 1,
        limit: 10,
      };
      return {
        ...state,
        loadingMutation: false,
        postsByUser: {
          ...state.postsByUser,
          [key]: {
            ...prev,
            items: [post, ...(prev.items || [])],
            total: (prev.total || 0) + 1,
          },
        },
      };
    }
    case types.UPDATE_POST_SUCCESS: {
      const post = action.payload;
      const postId = post?._id;
      const nextByUser = { ...state.postsByUser };
      Object.keys(nextByUser).forEach((key) => {
        nextByUser[key] = {
          ...nextByUser[key],
          items: updatePostInItems(nextByUser[key].items, postId, () => post),
        };
      });
      return {
        ...state,
        loadingMutation: false,
        postsByUser: nextByUser,
        currentPost:
          state.currentPost?._id === postId ? post : state.currentPost,
      };
    }
    case types.DELETE_POST_SUCCESS: {
      const postId = action.payload;
      const nextByUser = { ...state.postsByUser };
      Object.keys(nextByUser).forEach((key) => {
        nextByUser[key] = {
          ...nextByUser[key],
          items: (nextByUser[key].items || []).filter(
            (p) =>
              (p._id?.toString?.() ?? p.id) !==
              (postId?.toString?.() ?? postId),
          ),
          total: Math.max(0, (nextByUser[key].total || 0) - 1),
        };
      });
      return {
        ...state,
        loadingMutation: false,
        postsByUser: nextByUser,
        currentPost:
          state.currentPost?._id === postId ? null : state.currentPost,
      };
    }
    case types.CREATE_POST_FAIL:
    case types.UPDATE_POST_FAIL:
    case types.DELETE_POST_FAIL:
      return { ...state, loadingMutation: false, error: action.payload };

    case types.POST_LIKE_REQUEST:
    case types.POST_UNLIKE_REQUEST: {
      const postId = action.payload;
      const likedByMe = action.type === types.POST_LIKE_REQUEST;
      const nextByUser = { ...state.postsByUser };
      Object.keys(nextByUser).forEach((key) => {
        nextByUser[key] = {
          ...nextByUser[key],
          items: updatePostInItems(nextByUser[key].items, postId, (p) => {
            const count = p.likesCount ?? p.LikesCount ?? p.Likes?.length ?? 0;
            return { ...p, likedByMe, likesCount: Math.max(0, count + (likedByMe ? 1 : -1)) };
          }),
        };
      });
      const current = state.currentPost;
      const nextCurrent = current && (current._id?.toString?.() ?? current.id) === (postId?.toString?.() ?? postId)
        ? { ...current, likedByMe, likesCount: Math.max(0, (current.likesCount ?? current.LikesCount ?? current.Likes?.length ?? 0) + (likedByMe ? 1 : -1)) }
        : current;
      return { ...state, postsByUser: nextByUser, currentPost: nextCurrent };
    }

    case types.POST_LIKE_FAIL:
    case types.POST_UNLIKE_FAIL: {
      const postId = action.meta?.postId ?? action.payload?.postId;
      if (!postId) return { ...state, error: action.payload };
      const wasLike = action.type === types.POST_LIKE_FAIL;
      const nextByUser = { ...state.postsByUser };
      Object.keys(nextByUser).forEach((key) => {
        nextByUser[key] = {
          ...nextByUser[key],
          items: updatePostInItems(nextByUser[key].items, postId, (p) => {
            const count = p.likesCount ?? p.LikesCount ?? p.Likes?.length ?? 0;
            return { ...p, likedByMe: !wasLike, likesCount: Math.max(0, count + (wasLike ? -1 : 1)) };
          }),
        };
      });
      const current = state.currentPost;
      const nextCurrent = current && (current._id?.toString?.() ?? current.id) === (postId?.toString?.() ?? postId)
        ? { ...current, likedByMe: !wasLike, likesCount: Math.max(0, (current.likesCount ?? current.LikesCount ?? current.Likes?.length ?? 0) + (wasLike ? -1 : 1)) }
        : current;
      return { ...state, postsByUser: nextByUser, currentPost: nextCurrent, error: action.payload };
    }

    case types.POST_LIKE_SUCCESS:
    case types.POST_UNLIKE_SUCCESS: {
      const { postId, likesCount } = action.payload;
      const likedByMe = action.type === types.POST_LIKE_SUCCESS;
      const nextByUser = { ...state.postsByUser };
      Object.keys(nextByUser).forEach((key) => {
        nextByUser[key] = {
          ...nextByUser[key],
          items: updatePostInItems(nextByUser[key].items, postId, (p) => ({
            ...p,
            Likes: p.Likes ?? [],
            likesCount: likesCount ?? p.Likes?.length ?? 0,
            likedByMe,
          })),
        };
      });
      return {
        ...state,
        postsByUser: nextByUser,
        currentPost:
          state.currentPost?._id?.toString() === postId?.toString()
            ? {
                ...state.currentPost,
                Likes: state.currentPost.Likes ?? [],
                likesCount: likesCount ?? state.currentPost.Likes?.length,
                likedByMe,
              }
            : state.currentPost,
      };
    }

    case types.POST_ADD_COMMENT_SUCCESS: {
      const { postId, comment } = action.payload || {};
      if (!postId || !comment) return state;
      const nextByUser = { ...state.postsByUser };
      Object.keys(nextByUser).forEach((key) => {
        nextByUser[key] = {
          ...nextByUser[key],
          items: updatePostInItems(nextByUser[key].items, postId, (p) => ({
            ...p,
            Comments: [...(p.Comments || []), comment],
          })),
        };
      });
      return { ...state, postsByUser: nextByUser };
    }
    case types.POST_ADD_REPLY_SUCCESS: {
      const { postId, commentId, reply } = action.payload || {};
      if (!postId || !commentId || !reply) return state;
      const nextByUser = { ...state.postsByUser };
      Object.keys(nextByUser).forEach((key) => {
        nextByUser[key] = {
          ...nextByUser[key],
          items: updatePostInItems(nextByUser[key].items, postId, (p) => {
            const comments = (p.Comments || []).map((c) => {
              const cId = c._id?.toString?.() ?? c.id;
              if (cId !== (commentId?.toString?.() ?? commentId)) return c;
              return { ...c, Replies: [...(c.Replies || []), reply] };
            });
            return { ...p, Comments: comments };
          }),
        };
      });
      return { ...state, postsByUser: nextByUser };
    }
    case types.POST_DELETE_COMMENT_SUCCESS: {
      const { postId, commentId } = action.payload || {};
      if (!postId || !commentId) return state;
      const nextByUser = { ...state.postsByUser };
      Object.keys(nextByUser).forEach((key) => {
        nextByUser[key] = {
          ...nextByUser[key],
          items: updatePostInItems(nextByUser[key].items, postId, (p) => ({
            ...p,
            Comments: (p.Comments || []).filter(
              (c) =>
                (c._id?.toString?.() ?? c.id) !==
                (commentId?.toString?.() ?? commentId),
            ),
          })),
        };
      });
      return { ...state, postsByUser: nextByUser };
    }

    case types.POST_VOTE_POLL_SUCCESS: {
      const { postId, poll } = action.payload;
      const nextByUser = { ...state.postsByUser };
      Object.keys(nextByUser).forEach((key) => {
        nextByUser[key] = {
          ...nextByUser[key],
          items: updatePostInItems(nextByUser[key].items, postId, (p) => ({
            ...p,
            Poll: poll,
          })),
        };
      });
      return {
        ...state,
        postsByUser: nextByUser,
        currentPost:
          state.currentPost?._id?.toString() === postId?.toString()
            ? { ...state.currentPost, Poll: poll }
            : state.currentPost,
      };
    }

    case types.UPLOAD_POST_MEDIA_REQUEST:
      return { ...state, error: null };
    case types.UPLOAD_POST_MEDIA_SUCCESS:
    case types.UPLOAD_POST_MEDIA_FAIL:
      return state;

    case types.RECORD_POST_VIEW_SUCCESS: {
      const { postId } = action.payload || {};
      if (!postId) return state;
      const nextByUser = { ...state.postsByUser };
      Object.keys(nextByUser).forEach((key) => {
        nextByUser[key] = {
          ...nextByUser[key],
          items: updatePostInItems(nextByUser[key].items, postId, (p) => ({
            ...p,
            viewersCount: (p.viewersCount ?? 0) + 1,
          })),
        };
      });
      return {
        ...state,
        postsByUser: nextByUser,
        currentPost:
          state.currentPost?._id?.toString() === postId?.toString()
            ? {
                ...state.currentPost,
                viewersCount: (state.currentPost.viewersCount ?? 0) + 1,
              }
            : state.currentPost,
      };
    }

    case types.FETCH_FEATURED_BY_USER_REQUEST:
      return { ...state, loadingFeatured: true, error: null };
    case types.FETCH_FEATURED_BY_USER_SUCCESS: {
      const { userId, items } = action.payload;
      return {
        ...state,
        loadingFeatured: false,
        featuredByUser: {
          ...state.featuredByUser,
          [userId]: { items: items ?? [] },
        },
      };
    }
    case types.FETCH_FEATURED_BY_USER_FAIL:
      return { ...state, loadingFeatured: false, error: action.payload };

    case types.FETCH_FEATURED_BY_ID_REQUEST:
      return { ...state, loadingFeatured: true, error: null };
    case types.FETCH_FEATURED_BY_ID_SUCCESS:
      return {
        ...state,
        loadingFeatured: false,
        currentFeatured: action.payload,
      };
    case types.FETCH_FEATURED_BY_ID_FAIL:
      return { ...state, loadingFeatured: false, error: action.payload };

    case types.CREATE_FEATURED_SUCCESS: {
      const highlight = action.payload;
      const userId =
        highlight?.UserID?._id ?? highlight?.UserID ?? highlight?.userID;
      if (!userId) return { ...state };
      const key = userId.toString?.() ?? userId;
      const prev = state.featuredByUser[key] ?? { items: [] };
      return {
        ...state,
        featuredByUser: {
          ...state.featuredByUser,
          [key]: { items: [...(prev.items || []), highlight] },
        },
      };
    }
    case types.UPDATE_FEATURED_SUCCESS: {
      const highlight = action.payload;
      const userId =
        highlight?.UserID?._id ?? highlight?.UserID ?? highlight?.userID;
      if (!userId) return { ...state };
      const key = userId.toString?.() ?? userId;
      const prev = state.featuredByUser[key] ?? { items: [] };
      const items = (prev.items || []).map((h) =>
        (h._id ?? h.id)?.toString() ===
        (highlight._id ?? highlight.id)?.toString()
          ? highlight
          : h,
      );
      return {
        ...state,
        featuredByUser: { ...state.featuredByUser, [key]: { items } },
        currentFeatured:
          state.currentFeatured?._id?.toString() ===
          (highlight._id ?? highlight.id)?.toString()
            ? highlight
            : state.currentFeatured,
      };
    }
    case types.DELETE_FEATURED_SUCCESS: {
      const { highlightId, userId } = action.payload;
      if (!userId) return { ...state };
      const key = userId.toString?.() ?? userId;
      const prev = state.featuredByUser[key] ?? { items: [] };
      const items = (prev.items || []).filter(
        (h) =>
          (h._id ?? h.id)?.toString() !==
          (highlightId?.toString() ?? highlightId),
      );
      return {
        ...state,
        featuredByUser: { ...state.featuredByUser, [key]: { items } },
        currentFeatured:
          state.currentFeatured?._id?.toString() ===
          (highlightId?.toString() ?? highlightId)
            ? null
            : state.currentFeatured,
      };
    }
    case types.ADD_POST_TO_FEATURED_SUCCESS: {
      const { highlightId, userId, highlight } = action.payload;
      if (!userId || !highlight) return { ...state };
      const key = userId.toString?.() ?? userId;
      const prev = state.featuredByUser[key] ?? { items: [] };
      const items = (prev.items || []).map((h) =>
        (h._id ?? h.id)?.toString() === (highlightId?.toString() ?? highlightId)
          ? highlight
          : h,
      );
      return {
        ...state,
        featuredByUser: { ...state.featuredByUser, [key]: { items } },
        currentFeatured:
          state.currentFeatured?._id?.toString() ===
          (highlightId?.toString() ?? highlightId)
            ? highlight
            : state.currentFeatured,
      };
    }
    case types.REMOVE_POST_FROM_FEATURED_SUCCESS: {
      const { highlightId, postId, userId } = action.payload;
      if (!userId) return { ...state };
      const key = userId.toString?.() ?? userId;
      const prev = state.featuredByUser[key] ?? { items: [] };
      const highlight = (prev.items || []).find(
        (h) =>
          (h._id ?? h.id)?.toString() ===
          (highlightId?.toString() ?? highlightId),
      );
      if (!highlight) return state;
      const nextPostIds = (highlight.PostIds || []).filter(
        (id) => id?.toString() !== (postId?.toString() ?? postId),
      );
      const updatedHighlight = { ...highlight, PostIds: nextPostIds };
      const items = (prev.items || []).map((h) =>
        (h._id ?? h.id)?.toString() === (highlightId?.toString() ?? highlightId)
          ? updatedHighlight
          : h,
      );
      return {
        ...state,
        featuredByUser: { ...state.featuredByUser, [key]: { items } },
        currentFeatured:
          state.currentFeatured?._id?.toString() ===
          (highlightId?.toString() ?? highlightId)
            ? {
                ...state.currentFeatured,
                PostIds: nextPostIds,
                posts: (state.currentFeatured?.posts || []).filter(
                  (p) =>
                    (p._id ?? p.id)?.toString() !==
                    (postId?.toString() ?? postId),
                ),
              }
            : state.currentFeatured,
      };
    }

    case types.RESET_POSTS_ERROR:
      return { ...state, error: null };

    default:
      return state;
  }
};
